// ===========================================
// Rendered preview panel
// ===========================================
//
// Shows a server-rendered email exactly as a family would receive it —
// branding, wrapper and all — rather than the raw body an editor holds.
//
// Generalised from the preview block in `TemplateEditorDialog` (2026-09-17). It
// takes settled content plus the three query states, because a preview is a
// network call: without them a failed fetch renders as an EMPTY PANEL, which
// looks exactly like an email with no body and invites the admin to "fix" copy
// that was never the problem.

import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';

export interface RenderedPreviewPanelProps {
  /**
   * Heading for the panel. Name WHAT is being previewed where a page can show
   * several things — a panel that just says "Preview" beside an editor showing
   * a different item is worse than no panel.
   */
  title?: string;
  /** The rendered subject line, shown above the body as a mail client would. */
  subject?: string;
  /** Rendered HTML. ⚠️ Sanitised here before it is ever injected. */
  html?: string;
  isLoading?: boolean;
  isError?: boolean;
  /** Shown instead of the panel before anything has been requested. */
  placeholder?: string;
  maxHeight?: number;
}

export default function RenderedPreviewPanel({
  title = 'Preview',
  subject,
  html,
  isLoading,
  isError,
  placeholder = 'Select an email to preview it.',
  maxHeight = 420,
}: RenderedPreviewPanelProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : isError ? (
        /* Distinguished from "empty" deliberately — see the header note. */
        <Alert severity="error">Could not render this preview.</Alert>
      ) : !html ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {placeholder}
        </Typography>
      ) : (
        <>
          {subject !== undefined && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Subject
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {subject}
              </Typography>
            </Box>
          )}
          {/*
            ⚠️ DOMPurify IS NOT OPTIONAL. The backend sanitises on save, but this
            panel renders whatever the endpoint returns, and `dangerouslySetInnerHTML`
            is the one place in this app where a stored-XSS payload would
            execute. Sanitising here too means the guarantee does not depend on
            every future writer of that endpoint.
          */}
          <Box
            data-testid="rendered-preview-body"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
            sx={{
              maxHeight,
              overflow: 'auto',
              '& a': { color: 'primary.main' },
              '& img': { maxWidth: '100%' },
            }}
          />
        </>
      )}
    </Paper>
  );
}
